import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { Observable, combineLatest, map } from 'rxjs';
import { viewpointBuilderFormattingOperations } from './viewpointBuilderFormattingOperations';
import {
  createFormattingRule,
  updateFormattingRule,
  deleteFormattingRule,
  reorderFormattingRules,
  reorderLabelFormatting,
  createLabelFormatting,
  deleteLabelFormatting,
  showReferenceTypeToggled,
  updateLabelFormattingAt,
} from 'perspective/perspectiveEditor/perspectivesEditor$/actions';
import {
  setFormattingOptions,
  setFormattingRules,
  setLabelFormatting,
  resetFormattingState,
  setMigratedFormattingRules,
  setKeepOriginalColor,
} from './viewpointBuilderFormattingActions';
import {
  ViewpointFormattingState,
  ViewpointFormattingProps,
} from './viewpointFormattingTypes';
import { mapToViewpointBuilderFormattingViewState } from './mapToViewpointBuilderFormattingViewState';
import { SelectedViewState } from '../selectedView/types';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { globalFields$ } from 'streams/fields/globalFields$';
import { fields$ } from 'streams/fields/fields$';
import { getNonTemplateFieldsByName } from 'streams/fields/getNonTemplateFieldsByName';
import { buildFieldDataByRuleId } from 'perspectiveSidebar/perspectiveEditor/getFormattingForInvalidRuleByRuleId';
import { getViewpointBuilderFormattingCommands } from './viewpointBuilderFormattingCommands';

export const createViewpointFormattingState$ = (
  namespace: string,
  selectedView$: Observable<SelectedViewState>
): Observable<ViewpointFormattingState> =>
  reducedStream<ViewpointFormattingState>(
    `viewpointBuilder2.0/viewpointFormatting$:${namespace}`,
    viewpointBuilderFormattingOperations.getInitialState(),
    [
      streamReducer(
        selectedView$,
        viewpointBuilderFormattingOperations.setViewName
      ),
      reducer(
        showReferenceTypeToggled,
        viewpointBuilderFormattingOperations.showReferenceTypeToggled
      ),
      reducer(
        updateLabelFormattingAt,
        viewpointBuilderFormattingOperations.updateLabelFormattingAt
      ),
      reducer(
        deleteLabelFormatting,
        viewpointBuilderFormattingOperations.deleteLabelFormatting
      ),
      reducer(
        reorderLabelFormatting,
        viewpointBuilderFormattingOperations.reorderLabelFormatting
      ),
      reducer(
        createLabelFormatting,
        viewpointBuilderFormattingOperations.createLabelFormatting
      ),
      reducer(
        createFormattingRule,
        viewpointBuilderFormattingOperations.createFormattingRule
      ),
      reducer(
        updateFormattingRule,
        viewpointBuilderFormattingOperations.updateFormattingRule
      ),
      reducer(
        deleteFormattingRule,
        viewpointBuilderFormattingOperations.deleteFormattingRule
      ),
      reducer(
        reorderFormattingRules,
        viewpointBuilderFormattingOperations.reorderFormattingRules
      ),
      reducer(
        setFormattingOptions,
        viewpointBuilderFormattingOperations.setFormattingOptions
      ),
      reducer(
        setFormattingRules,
        viewpointBuilderFormattingOperations.setFormattingRules
      ),
      reducer(
        setMigratedFormattingRules,
        viewpointBuilderFormattingOperations.setMigratedFormattingRules
      ),
      reducer(
        setLabelFormatting,
        viewpointBuilderFormattingOperations.setLabelFormatting
      ),
      reducer(
        resetFormattingState,
        viewpointBuilderFormattingOperations.resetFormattingState
      ),
      reducer(
        setKeepOriginalColor,
        viewpointBuilderFormattingOperations.setKeepOriginalColor
      ),
    ],
    { namespace }
  );

export const createViewpointFormatting$ = (
  namespace: string,
  viewpointFormattingState$: Observable<ViewpointFormattingState>
): Observable<ViewpointFormattingProps> => {
  const commands = getViewpointBuilderFormattingCommands(namespace);
  return combineLatest({
    state: viewpointFormattingState$,
    locale: locale$,
    permissionContext: currentUserPermissionContext$,
    i18nLocale: i18nLocale$,
    globalFields: globalFields$,
    fields: fields$,
  }).pipe(
    map(
      ({
        state,
        locale,
        permissionContext,
        i18nLocale,
        globalFields,
        fields,
      }) => {
        const fieldDataByRuleId = buildFieldDataByRuleId(
          state.formattingTab.conditionalFormattingRules,
          globalFields.byName,
          getNonTemplateFieldsByName(fields.byName)
        );
        return mapToViewpointBuilderFormattingViewState(
          state,
          locale,
          permissionContext,
          i18nLocale,
          fieldDataByRuleId,
          fields.byName,
          commands
        );
      }
    )
  );
};
